/**
 * Settings → **Image generation** — connect the operator's OWN Higgsfield subscription, and
 * choose which engine a run uses.
 *
 * **ADMINS ONLY since WS-HF shared login.** The designers are the company's own team, and every
 * one of them generates on the ONE company subscription a super-admin connects in Admin → AI
 * providers. So a designer never sees this section, or any Higgsfield connect button: the entry
 * stays out of their sub-nav, and their machine never asks the keychain. A tenant admin sees
 * only a pointer to the company connection. A super-admin keeps the per-computer connection,
 * labelled "This computer only (advanced)", for running a job on their own account.
 *
 * DESKTOP ONLY. The block reports `onAvailable(false)` on web, which keeps its entry out of
 * the Settings sub-nav entirely — the same "renders nothing unless it HAS something" contract
 * `LegacyConnectors` and `SessionsPanel` follow. There is no MCP surface in the web build to
 * connect to (`@genz/platform`'s `hfMcpAvailable`), and a browser cannot hold a third party's
 * paid-account credential safely.
 *
 * The sign-in is out-of-band by design: Rust hands back a verification URL (and, on the
 * device-code flow, a short code), the operator finishes in their own browser, and this panel
 * polls until Rust says "connected". Nothing about the grant — no token, no code — passes
 * through this component.
 */
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { higgsfieldMcp, type HfMcpLoginStart, type HfMcpStatus } from "@genz/platform";
import {
  getGenerationSourceChoice,
  readCompanySubscriptionReady,
  resolveGenerationSource,
  setGenerationSourceChoice,
  type CapabilitiesReader,
  type GenerationSource,
} from "../stores/generationSource.js";
import { InfoHint } from "../ui/Tooltip.js";
import { storageAvailable } from "../generate/deviceRunRegistry.js";
import { useSessionStore } from "../stores/session.js";

/** Fallback poll cadence when Rust does not name one. */
const POLL_MS = 3000;

export function HiggsfieldConnection({ active = true, onAvailable, client }: {
  /** False while another Settings section is open. The block still MOUNTS (its availability
   *  is what puts the entry in the sub-nav) and simply draws nothing until it is open. */
  active?: boolean;
  /** Whether this build has a Higgsfield surface at all — false on web. */
  onAvailable?: (available: boolean) => void;
  /** Reads whether the company subscription is connected; while it is, Generate runs on it. */
  client?: CapabilitiesReader;
} = {}) {
  const mcp = higgsfieldMcp();
  const isAdmin = useSessionStore((st) => st.isAdmin());
  const isSuper = useSessionStore((st) => st.isSuperAdmin());
  /* The section exists for an ADMIN on a desktop build. `mcpUsable` is narrower: only a
     super-admin's machine talks to its own keychain at all. */
  const available = mcp.hfMcpAvailable && isAdmin;
  const mcpUsable = mcp.hfMcpAvailable && isSuper;
  const [status, setStatus] = useState<HfMcpStatus | null>(null);
  const [login, setLogin] = useState<HfMcpLoginStart | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [choice, setChoice] = useState<GenerationSource | undefined>(getGenerationSourceChoice);
  const [companyReady, setCompanyReady] = useState(false);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  /** Ties each radio to its own label. `useId`, not a constant: the Editor tab mounts the
   *  Settings panel a second time, and two `id="…-cloud"` would make the first label win
   *  both clicks. */
  const ids = useId();
  /** WS-HF N7 — can this device remember a submission across a reload? Probed once, by
   *  WRITING: a `localStorage` object whose `setItem` throws is exactly the case that matters. */
  const [canRemember] = useState(storageAvailable);

  useEffect(() => { onAvailable?.(available); }, [onAvailable, available]);

  const refresh = useCallback(async () => {
    if (!mcpUsable) return;
    try {
      setStatus(await mcp.hfMcpStatus());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [mcpUsable, mcp]);

  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    if (!mcpUsable || !client) return;
    let cancelled = false;
    void readCompanySubscriptionReady(client).then((ready) => { if (!cancelled) setCompanyReady(ready); });
    return () => { cancelled = true; };
  }, [mcpUsable, client]);

  // Stop polling when the panel goes away — a timer that outlives the component would keep
  // hitting the vendor's token endpoint after the operator navigated off.
  useEffect(() => () => { if (pollTimer.current) clearTimeout(pollTimer.current); }, []);

  const connected = !!status?.connected;
  const effective = resolveGenerationSource(mcpUsable && !companyReady, connected, choice);

  /** One poll tick; re-arms itself until Rust reports `connected` or throws. */
  const pollOnce = useCallback(async () => {
    try {
      const r = await mcp.hfMcpLoginPoll();
      if (r.status === "connected") {
        setLogin(null);
        setBusy(false);
        await refresh();
        return;
      }
      pollTimer.current = setTimeout(() => void pollOnce(), (r.afterSecs ?? 0) * 1000 || POLL_MS);
    } catch (e) {
      // A terminal OAuth failure ends the attempt in Rust too, so do NOT re-arm.
      setError(e instanceof Error ? e.message : String(e));
      setLogin(null);
      setBusy(false);
    }
  }, [mcp, refresh]);

  const connect = async () => {
    setError(null);
    setBusy(true);
    try {
      const start = await mcp.hfMcpLoginStart();
      setLogin(start);
      pollTimer.current = setTimeout(() => void pollOnce(), POLL_MS);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  const openVerification = async () => {
    if (!login) return;
    setError(null);
    try {
      await mcp.hfMcpOpenLogin(login.verificationUri);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const cancel = () => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
    setLogin(null);
    setBusy(false);
    // Tell RUST as well. Clearing local state only stops the polling; the loopback listener
    // would keep a registered port for its whole 15-minute deadline, and the next attempt
    // needs that port.
    void mcp.hfMcpLoginCancel().catch(() => { /* nothing in flight — harmless */ });
  };

  const disconnect = async () => {
    setError(null);
    setBusy(true);
    try {
      await mcp.hfMcpLogout();
      // The stored preference is NOT cleared: reconnecting later should restore the
      // operator's choice rather than silently reset it. `resolveGenerationSource` already
      // falls back to the cloud while disconnected.
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const pick = (v: GenerationSource) => {
    setChoice(v);
    setGenerationSourceChoice(v);
  };

  if (!available || !active) return null;

  /* The company connection lives in Admin, for every designer at once. This line says so to
     both admin roles; a tenant admin has nothing else here. */
  const pointer = (
    <p className="caption" data-testid="hf-company-pointer">
      {isSuper
        ? "Designers generate on the company Higgsfield subscription. Connect it once in Admin → AI providers."
        : "Your workspace manages image creation. Ask your workspace admin if you need help with access."}
    </p>
  );

  if (!isSuper) {
    return (
      <div className="settings__block" data-testid="higgsfield-connection">
        <h3>Image generation</h3>
        {pointer}
      </div>
    );
  }

  return (
    <div className="settings__block" data-testid="higgsfield-connection">
      <h3>Image generation</h3>
      {pointer}

      <h4 className="settings__subheading" data-testid="hf-this-computer">
        This computer only (advanced){" "}
        <InfoHint text="Your own Higgsfield sign-in, on this computer only. Jobs you run here are billed to that account, not to the company subscription. Designers never see this." />
      </h4>

      {error && (
        <p className="banner banner--danger" role="alert" data-testid="hf-error">{error}</p>
      )}

      {/* WS-HF N7 — the app protects a retry from being billed twice by remembering what it
          submitted. On a device where it cannot remember, say so here rather than let the
          operator assume a guarantee that is not there. */}
      {!canRemember && (
        <p className="banner banner--danger" data-testid="hf-no-storage">
          This computer cannot remember submissions; retries may be billed twice.
        </p>
      )}

      {connected ? (
        <div className="setting-row" data-testid="hf-connected">
          <div>
            <p>
              Connected to Higgsfield
              {status?.plan ? ` · ${status.plan} plan` : ""}
              {status?.credits !== undefined ? ` · ${status.credits} credits` : ""}
            </p>
            {status?.error && (
              <p className="faint" data-testid="hf-balance-note">{status.error}</p>
            )}
          </div>
          <button type="button" className="btn btn--sm btn--ghost" disabled={busy}
            data-testid="hf-disconnect" onClick={() => void disconnect()}>
            Disconnect
          </button>
        </div>
      ) : login ? (
        <div className="setting-row" data-testid="hf-login">
          <div>
            {login.userCode ? (
              <p>
                Open the page below and enter this code:{" "}
                <strong data-testid="hf-user-code">{login.userCode}</strong>
              </p>
            ) : (
              <p>Finish signing in to Higgsfield in your browser, then come back here.</p>
            )}
            <p className="faint" data-testid="hf-login-wait">Waiting for you to finish…</p>
          </div>
          <button type="button" className="btn btn--sm" data-testid="hf-open-verification"
            onClick={() => void openVerification()}>
            Open Higgsfield
          </button>
          <button type="button" className="btn btn--sm btn--ghost" data-testid="hf-cancel"
            onClick={cancel}>
            Cancel
          </button>
        </div>
      ) : (
        <div className="setting-row" data-testid="hf-disconnected">
          <div>
            <p>Not connected. This computer generates in the cloud, like every designer.</p>
            {status?.error && <p className="faint" data-testid="hf-status-note">{status.error}</p>}
          </div>
          <button type="button" className="btn btn--sm" disabled={busy}
            data-testid="hf-connect" onClick={() => void connect()}>
            {busy ? "Connecting…" : "Connect Higgsfield"}
          </button>
        </div>
      )}

      <fieldset className="field" data-testid="hf-source">
        <legend>Generate with</legend>
        <label htmlFor={`${ids}-cloud`}>
          <input type="radio" name={`generation-source-${ids}`} value="cloud"
            id={`${ids}-cloud`} data-testid="hf-source-cloud"
            aria-label="The cloud (platform plan)"
            checked={effective === "cloud"}
            onChange={() => pick("cloud")} />
          <span>The cloud (platform plan)</span>
        </label>
        <label htmlFor={`${ids}-mcp`}>
          <input type="radio" name={`generation-source-${ids}`} value="higgsfield-mcp"
            id={`${ids}-mcp`} data-testid="hf-source-mcp"
            aria-label="This computer, on my Higgsfield subscription"
            checked={effective === "higgsfield-mcp"}
            disabled={!connected || companyReady}
            onChange={() => pick("higgsfield-mcp")} />
          <span>This computer, on my Higgsfield subscription</span>
        </label>
        {companyReady && (
          <p className="faint" data-testid="hf-source-company">
            The company subscription is connected, so Generate runs on it. This computer&apos;s
            sign-in is used only while it is not connected.
          </p>
        )}
        {!connected && (
          <p className="faint" data-testid="hf-source-na">
            Connect Higgsfield to generate on your own subscription.
          </p>
        )}
      </fieldset>
    </div>
  );
}
